import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

const RETURN_TO_KEY = 'uniportal-return-to';

function FullScreenSpinner() {
  return (
    <div className="min-h-dvh paper-wash flex items-center justify-center">
      <div className="w-10 h-10 rounded-full border-[3px] border-slate-200 border-t-brand-rose animate-spin" />
    </div>
  );
}

/** Requires an authenticated session; mid-login states bounce to their screen.
 *  Preserves the intended deep link (e.g. a bookmarked /view/:id) across the
 *  login flow via per-tab sessionStorage — every post-login landing passes
 *  back through here, where the saved path is consumed. */
export function ProtectedRoute({
  children,
  publisherOnly = false,
  platformOnly = false,
}: {
  children: ReactNode;
  publisherOnly?: boolean;
  platformOnly?: boolean;
}) {
  const { state, canPublish, isPlatformAdmin } = useAuth();
  const location = useLocation();

  if (state.status === 'loading') return <FullScreenSpinner />;

  if (state.status !== 'authenticated') {
    // Remember where the user wanted to go (skip for the bare dashboard).
    if (location.pathname !== '/') {
      sessionStorage.setItem(RETURN_TO_KEY, location.pathname + location.search);
    }
    if (state.status === 'totp_setup') return <Navigate to="/totp-setup" replace />;
    if (state.status === 'totp_verify') return <Navigate to="/2fa" replace />;
    if (state.status === 'password_expired') return <Navigate to="/force-change-password" replace />;
    return <Navigate to="/login" replace />;
  }

  // Authenticated: consume a saved deep link once.
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY);
  if (returnTo) {
    sessionStorage.removeItem(RETURN_TO_KEY);
    if (returnTo !== location.pathname + location.search) {
      return <Navigate to={returnTo} replace />;
    }
  }

  if (platformOnly && !isPlatformAdmin) return <Navigate to="/" replace />;
  if (publisherOnly && !canPublish) return <Navigate to="/" replace />;

  return <>{children}</>;
}
